<template>
    <CCard style="height: 100%">
        <CCardHeader>
            <CIcon :src="Logo" />
            <p class="title mb-0 d-inline-block ml-2">Achieved Savings</p>
        </CCardHeader>
        <CCardBody>
            <p class="graph-title">Year over year impact</p>
            <CRow>
                <CCol sm="6">
                    <PieChart
                        id="pie-chart"
                        :chart-data="lastYearDataset"
                        :key="1"
                        :legend="false"
                    ></PieChart>
                    <p class="year-label mb-0">2020</p>
                    <p class="year-label-sub">Income</p>
                </CCol>
                <CCol sm="6"
                    ><PieChart
                        :chart-data="currentYearDataset"
                        :key="2"
                        :legend="false"
                    ></PieChart>
                    <p class="year-label mb-0">2021</p>
                    <p class="year-label-sub">Income</p>
                </CCol>
            </CRow>
            <p class="graph-title">Utilization impacts</p>
            <CRow>
                <CCol>
                    <CSelect
                        class="float-right"
                        :options="YEARS"
                        :value.sync="yearFilter"
                    />
                    <DoubleBarChart
                        :chart-data="monthChartDataset"
                        :key="2"
                        :legend="false"
                        style="height: 200px"
                    />
                </CCol>
            </CRow>
        </CCardBody>
        <CElementCover v-if="spinner" :opacity="0.8" />
    </CCard>
</template>

<script>
import PieChart from "../../../components/charts/PieChart1";
import DoubleBarChart from "../../../components/charts/DoubleBarChart1";
import gql from "graphql-tag";
import Logo from "../../../containers/sidebar-icons/Opportunity Dashboard.svg";
import _ from "../../Utility/utility";

export default {
    components: {
        PieChart,
        DoubleBarChart,
    },
    data() {
        return {
            Logo,
            lastYearData: [],
            currentYearData: [],
            gradient: "",
            gradient2: "",

            monthWiseExpense: [],
            monthWiseSavings: [],

            MONTHS,
            YEARS,
            spinner: false,
            yearFilter: new Date().getFullYear(),
        };
    },
    computed: {
        lastYearDataset() {
            return {
                datasets: [
                    {
                        data: this.lastYearData,
                        backgroundColor: this.gradient,
                    },
                ],
                labels: ["Savings", "Expense"],
            };
        },
        currentYearDataset() {
            return {
                datasets: [
                    {
                        data: this.currentYearData,
                        backgroundColor: this.gradient,
                    },
                ],
                labels: ["Savings", "Expense"],
            };
        },
        monthChartDataset() {
            return {
                datasets: [
                    {
                        data: this.monthWiseExpense,
                        backgroundColor: this.gradient2[0],
                    },
                    {
                        data: this.monthWiseSavings,
                        backgroundColor: this.gradient2[1],
                    },
                ],
                labels: MONTHS,
            };
        },
    },
    watch: {
        yearFilter() {
            this.getValueForBarChart();
        },
    },
    methods: {
        async init() {
            this.spinner = true;
            let currentYear = new Date().getFullYear();
            let lastYear = currentYear - 1;

            await this.getDataForPieChart(lastYear, currentYear);
            await this.getValueForBarChart();
            this.spinner = false;
        },
        // Pie chart
        async getDataForPieChart(lastYear, currentYear) {
            let lastYearSavings = await this.getSavings(null, lastYear);
            let currentYearSavings = await this.getSavings(null, currentYear);

            let lastYearExpense = await this.getExpense(null, lastYear);
            let currentYearExpense = await this.getExpense(null, currentYear);

            this.lastYearData = [lastYearSavings, lastYearExpense];
            this.currentYearData = [currentYearSavings, currentYearExpense];
        },
        // Bar chart
        async getValueForBarChart() {
            let monthWiseExpense = [],
                monthWiseSavings = [];
            for (let i = 1; i <= 12; i++) {
                monthWiseExpense.push(this.getExpense(i, this.yearFilter));
                monthWiseSavings.push(this.getSavings(i, this.yearFilter));
            }

            this.monthWiseExpense = await Promise.all(monthWiseExpense);
            this.monthWiseSavings = await Promise.all(monthWiseSavings);
        },
        async getExpense(month, year) {
            const res = await this.$apollo.mutate({
                mutation: GET_EXPENSE,
                variables: { month, year },
            });
            const totalExpense =
                res?.data?.spendmd_db_spendmd_data_date_filter_aggregate
                    ?.aggregate?.sum?.net_amount;
            if (totalExpense) return _.parseAmountToInt(totalExpense);
            return 0;
        },
        async getSavings(month, year) {
            const res = await this.$apollo.mutate({
                mutation: GET_SAVINGS,
                variables: { month, year },
            });
            const totalSavings =
                res?.data?.spendmd_db_project_date_filter_aggregate?.aggregate
                    ?.sum?.spendmd_opportunity;
            if (totalSavings) return _.parseAmountToInt(totalSavings);
            return 0;
        },
    },
    async beforeMount() {
        await this.init();
    },
    mounted() {
        this.gradient = [];
        let g1 = document
            .getElementById("doughnut-chart")
            .getContext("2d")
            .createLinearGradient(0, 0, 0, 220);

        g1.addColorStop(0.5, "rgba(255, 101, 100, 1)");
        g1.addColorStop(0.1, "rgba(255, 225, 133, 1)");
        let g2 = document
            .getElementById("doughnut-chart")
            .getContext("2d")
            .createLinearGradient(0, 0, 0, 220);

        g2.addColorStop(0.1, "rgb(133, 235, 255, 1)");
        g2.addColorStop(0.5, "rgb(100, 141, 255, 1)");

        this.gradient.push(g2);
        this.gradient.push(g1);
        // ----------------------------------------------------------------------- //
        this.gradient2 = [];
        let g21 = document
            .getElementById("bar-chart")
            .getContext("2d")
            .createLinearGradient(0, 0, 0, 220);

        g21.addColorStop(0.1, "rgba(243, 133, 255, 1)");
        g21.addColorStop(0.5, "rgba(191, 23, 242, 1)");
        let g22 = document
            .getElementById("bar-chart")
            .getContext("2d")
            .createLinearGradient(0, 0, 0, 220);

        g22.addColorStop(0.5, "rgba(133, 255, 179, 1)");
        g22.addColorStop(0.1, "rgba(61, 184, 139, 1)");

        this.gradient2.push(g21);
        this.gradient2.push(g22);
    },
};

const GET_EXPENSE = gql`
    query ($month: float8, $year: float8 = 2022) {
        spendmd_db_spendmd_data_date_filter_aggregate(
            where: { month: { _eq: $month }, year: { _eq: $year } }
        ) {
            aggregate {
                sum {
                    net_amount
                }
            }
        }
    }
`;

const GET_SAVINGS = gql`
    query ($month: float8, $year: float8) {
        spendmd_db_project_date_filter_aggregate(
            where: {
                month: { _eq: $month }
                year: { _eq: $year }
                project: { status: { _eq: "Completed" } }
            }
        ) {
            aggregate {
                sum {
                    spendmd_opportunity
                }
            }
        }
    }
`;
const YEARS = [
    2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030,
];
const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
</script>

<style scoped>
.year-label {
    text-align: center;
    font: normal normal 900 16px/23px Lato;
    letter-spacing: 0px;
    color: #1f2621;
    opacity: 1;
}
.year-label-sub {
    text-align: center;
    font: normal normal normal 9px/23px Lato;
    letter-spacing: 0.63px;
    color: #9ca8a9;
    opacity: 1;
}
.graph-title {
    text-align: left;
    font: normal normal bold 13px/24px Lato;
    letter-spacing: 0px;
    color: #1f2621;
    opacity: 1;
}
.title {
    text-align: left;
    font: normal normal normal 16px/24px Lato;
    letter-spacing: 0px;
    color: #1f2621;
    opacity: 1;
}
</style>